import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../App';
import { featuredFactories } from '../data/mockData';

const SuppliersPage = () => {
  const { openContactForm, showNotification } = useAppContext();
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedSpecialty, setSelectedSpecialty] = useState('all');
  const [sortBy, setSortBy] = useState('rating');

  const regions = ['all', 'China', 'India', 'Japan', 'Netherlands', 'USA'];
  const specialties = ['all', 'Organic Chemicals', 'Petrochemicals', 'Specialty Chemicals', 'Performance Products', 'Pharma Intermediates', 'Electronic Chemicals', 'Fertilizers', 'Industrial Chemicals', 'Bulk Chemicals', 'Agrochemicals'];

  const filteredSuppliers = useMemo(() => {
    let suppliers = [...featuredFactories];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      suppliers = suppliers.filter(s =>
        s.name?.toLowerCase().includes(query) ||
        s.location?.toLowerCase().includes(query) ||
        s.specialties?.some(spec => spec.toLowerCase().includes(query))
      );
    }

    if (selectedRegion !== 'all') {
      suppliers = suppliers.filter(s => s.location?.includes(selectedRegion));
    }

    if (selectedSpecialty !== 'all') {
      suppliers = suppliers.filter(s => s.specialties?.includes(selectedSpecialty));
    }

    suppliers.sort((a, b) => {
      switch (sortBy) {
        case 'rating': return (b.rating || 0) - (a.rating || 0);
        case 'name': return (a.name || '').localeCompare(b.name || '');
        case 'response': return (a.responseTime || '').localeCompare(b.responseTime || '');
        default: return 0;
      }
    });

    return suppliers;
  }, [searchQuery, selectedRegion, selectedSpecialty, sortBy]);

  const handleContact = (supplier) => {
    openContactForm(supplier.name);
  };

  const handleViewProfile = (supplier) => {
    showNotification(`Viewing profile of ${supplier.name}`, 'info');
    navigate(`/suppliers/${supplier.id}`);
  };

  return (
    <div className="suppliers-page">
      <div className="container">
        <div className="page-header">
          <div className="header-content">
            <h1 className="page-title">Verified Suppliers</h1>
            <p className="page-subtitle">Connect with trusted chemical suppliers from around the world</p>
          </div>
        </div>

        <div className="stats-banner">
          <div className="stat-card">
            <div className="stat-icon">🏭</div>
            <div className="stat-content">
              <span className="stat-value">{featuredFactories.length}+</span>
              <span className="stat-label">Verified Suppliers</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🌍</div>
            <div className="stat-content">
              <span className="stat-value">5</span>
              <span className="stat-label">Countries</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⭐</div>
            <div className="stat-content">
              <span className="stat-value">4.7+</span>
              <span className="stat-label">Avg Rating</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⚡</div>
            <div className="stat-content">
              <span className="stat-value">&lt;3h</span>
              <span className="stat-label">Avg Response</span>
            </div>
          </div>
        </div>

        <div className="filters-bar">
          <div className="search-box">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Search suppliers by name, location, or specialty..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <select value={selectedRegion} onChange={(e) => setSelectedRegion(e.target.value)}>
              {regions.map(region => (
                <option key={region} value={region}>
                  {region === 'all' ? 'All Regions' : region}
                </option>
              ))}
            </select>

            <select value={selectedSpecialty} onChange={(e) => setSelectedSpecialty(e.target.value)}>
              {specialties.map(spec => (
                <option key={spec} value={spec}>
                  {spec === 'all' ? 'All Specialties' : spec}
                </option>
              ))}
            </select>

            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="rating">Sort by Rating</option>
              <option value="name">Sort by Name</option>
              <option value="response">Sort by Response Time</option>
            </select>
          </div>
        </div>

        <div className="results-info">
          <span>{filteredSuppliers.length} suppliers found</span>
        </div>

        <div className="suppliers-grid">
          {filteredSuppliers.map((supplier, index) => (
            <div
              key={supplier.id}
              className="supplier-card"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="card-image">
                <img src={supplier.image} alt={supplier.name} />
                {supplier.verified && <span className="verified-badge">✓ Verified</span>}
              </div>

              <div className="card-body">
                <h3 className="supplier-name">{supplier.name}</h3>
                <p className="supplier-location">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 1.1-4.9 1.1-4.9-5.2 0-9.3 4.4-9.3 9.8 0 5.4 4.9 9.8 10.1 0 8.3 4.9 1.1 8.3 1.1" />
                  </svg>
                  {supplier.location}
                </p>

                <div className="specialties">
                  {supplier.specialties?.slice(0, 2).map(spec => (
                    <span key={spec} className="specialty-tag">{spec}</span>
                  ))}
                  {supplier.specialties?.length > 2 && (
                    <span className="specialty-tag more">+{supplier.specialties.length - 2}</span>
                  )}
                </div>

                <div className="metrics">
                  <div className="metric">
                    <span className="metric-icon">⭐</span>
                    <span className="metric-value">{supplier.rating}</span>
                  </div>
                  <div className="metric">
                    <span className="metric-icon">⚡</span>
                    <span className="metric-value">{supplier.responseTime}</span>
                  </div>
                </div>

                <div className="card-actions">
                  <button className="btn btn-outline" onClick={() => handleViewProfile(supplier)}>
                    View Profile
                  </button>
                  <button className="btn btn-primary" onClick={() => handleContact(supplier)}>
                    Contact
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="cta-section">
          <h2>Become a Verified Supplier</h2>
          <p>Join our network of trusted chemical suppliers and reach buyers worldwide</p>
          <button className="btn btn-accent">Apply Now</button>
        </div>
      </div>

      <style jsx>{`
        .suppliers-page {
          padding: var(--space-xl) 0 var(--space-3xl);
          min-height: 100vh;
        }

        .page-header {
          margin-bottom: var(--space-xl);
        }

        .page-title {
          font-size: var(--font-4xl);
          font-weight: 700;
          color: var(--color-neutral-50);
          margin-bottom: var(--space-sm);
        }

        .page-subtitle {
          color: var(--color-neutral-400);
          font-size: var(--font-lg);
        }

        .stats-banner {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: var(--space-md);
          margin-bottom: var(--space-xl);
        }

        .stat-card {
          display: flex;
          align-items: center;
          gap: var(--space-md);
          padding: var(--space-lg);
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: var(--radius-lg);
        }

        .stat-icon {
          font-size: 2rem;
        }

        .stat-value {
          display: block;
          font-size: var(--font-xl);
          font-weight: 700;
          color: var(--color-accent-400);
        }

        .stat-label {
          font-size: var(--font-xs);
          color: var(--color-neutral-500);
        }

        .filters-bar {
          display: flex;
          gap: var(--space-md);
          margin-bottom: var(--space-lg);
          flex-wrap: wrap;
          padding: var(--space-md);
          background: rgba(255, 255, 255, 0.03);
          border-radius: var(--radius-lg);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }

        .search-box {
          flex: 1;
          min-width: 250px;
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          padding: var(--space-sm) var(--space-md);
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: var(--radius-md);
        }

        .search-box svg {
          color: var(--color-neutral-500);
        }

        .search-box input {
          flex: 1;
          background: none;
          border: none;
          color: var(--color-neutral-100);
          font-size: var(--font-sm);
          outline: none;
        }

        .filter-group {
          display: flex;
          gap: var(--space-sm);
          flex-wrap: wrap;
        }

        .filter-group select {
          padding: var(--space-sm) var(--space-md);
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: var(--radius-md);
          color: var(--color-neutral-200);
          font-size: var(--font-sm);
          cursor: pointer;
        }

        .results-info {
          color: var(--color-neutral-400);
          font-size: var(--font-sm);
          margin-bottom: var(--space-md);
        }

        .suppliers-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: var(--space-lg);
        }

        .supplier-card {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: var(--radius-lg);
          overflow: hidden;
          transition: all var(--transition-base);
          animation: fadeInUp 0.4s ease forwards;
          opacity: 0;
        }

        .supplier-card:hover {
          transform: translateY(-4px);
          border-color: var(--color-accent-400);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
        }

        .card-image {
          position: relative;
          height: 160px;
          background: rgba(0, 0, 0, 0.3);
        }

        .card-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .verified-badge {
          position: absolute;
          top: var(--space-sm);
          right: var(--space-sm);
          padding: 4px 8px;
          background: rgba(16, 185, 129, 0.9);
          color: white;
          font-size: var(--font-xs);
          font-weight: 500;
          border-radius: var(--radius-sm);
        }

        .card-body {
          padding: var(--space-lg);
        }

        .supplier-name {
          font-size: var(--font-lg);
          font-weight: 600;
          color: var(--color-neutral-50);
          margin-bottom: var(--space-xs);
        }

        .supplier-location {
          display: flex;
          align-items: center;
          gap: var(--space-xs);
          font-size: var(--font-sm);
          color: var(--color-neutral-500);
          margin-bottom: var(--space-md);
        }

        .specialties {
          display: flex;
          flex-wrap: wrap;
          gap: var(--space-xs);
          margin-bottom: var(--space-md);
        }

        .specialty-tag {
          padding: 4px 8px;
          background: rgba(59, 130, 246, 0.15);
          color: var(--color-accent-300);
          font-size: var(--font-xs);
          border-radius: var(--radius-sm);
        }

        .specialty-tag.more {
          background: rgba(255, 255, 255, 0.1);
          color: var(--color-neutral-400);
        }

        .metrics {
          display: flex;
          gap: var(--space-lg);
          padding: var(--space-md);
          background: rgba(255, 255, 255, 0.03);
          border-radius: var(--radius-md);
          margin-bottom: var(--space-md);
        }

        .metric {
          display: flex;
          align-items: center;
          gap: var(--space-xs);
        }

        .metric-icon {
          font-size: 1rem;
        }

        .metric-value {
          font-size: var(--font-sm);
          font-weight: 600;
          color: var(--color-neutral-200);
        }

        .card-actions {
          display: flex;
          gap: var(--space-sm);
        }

        .card-actions .btn {
          flex: 1;
          padding: var(--space-sm);
          font-size: var(--font-xs);
        }

        .cta-section {
          margin-top: var(--space-3xl);
          padding: var(--space-2xl);
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(6, 182, 212, 0.2));
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: var(--radius-xl);
          text-align: center;
        }

        .cta-section h2 {
          font-size: var(--font-2xl);
          color: var(--color-neutral-50);
          margin-bottom: var(--space-sm);
        }

        .cta-section p {
          color: var(--color-neutral-400);
          margin-bottom: var(--space-lg);
        }

        .btn-accent {
          background: linear-gradient(135deg, var(--color-accent-300), var(--color-accent-400));
          color: var(--color-neutral-900);
          font-weight: 600;
          padding: var(--space-md) var(--space-xl);
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 1024px) {
          .stats-banner {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .filters-bar {
            flex-direction: column;
          }

          .search-box {
            min-width: 100%;
          }

          .suppliers-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default SuppliersPage;
